import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Container,
  alpha,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import CodeIcon from '@mui/icons-material/Code';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import TranslateIcon from '@mui/icons-material/Translate';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTranslation } from 'react-i18next';
import { toolCategories } from '../config/tools';
import { LANGUAGES, LANGUAGE_NAMES, pagePath } from '../seo/seoData';
import { toolAccent } from '../theme';
import { useThemeMode } from './ThemeModeProvider';
import CommandPalette from './CommandPalette';

export const HEADER_HEIGHT = 64;

interface HeaderProps {
  lang: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

const Header: React.FC<HeaderProps> = ({ lang }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { mode, toggleMode } = useThemeMode();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [langAnchor, setLangAnchor] = useState<HTMLElement | null>(null);

  const isActive = (slug: string) => location.pathname === pagePath(lang, slug);

  // Ctrl/Cmd+K anywhere, or "/" outside of text fields, opens the tool search.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const typing = target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if ((event.key === 'k' && (event.ctrlKey || event.metaKey)) || (event.key === '/' && !typing)) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Keep the user on the same page, only swap the language segment of the URL.
  const handleLanguageChange = (newLang: string) => {
    setLangAnchor(null);
    if (newLang === lang) return;
    const rest = location.pathname.split('/').slice(2).join('/');
    navigate(`/${newLang}${rest ? `/${rest}` : ''}`);
  };

  const themeLabel = mode === 'dark' ? t('layout.lightMode') : t('layout.darkMode');

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{
          zIndex: (th) => th.zIndex.drawer + 1,
          backgroundColor: alpha(theme.palette.background.default, 0.8),
          backdropFilter: 'blur(12px)',
          borderBottom: 1,
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: HEADER_HEIGHT, gap: 1 }}>
            <IconButton
              color="inherit"
              aria-label={t('common.openMenu')}
              edge="start"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <MenuIcon />
            </IconButton>

            <Box
              component={RouterLink}
              to={pagePath(lang)}
              aria-label={t('common.home')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'inherit',
                textDecoration: 'none',
                mr: 2,
                flexShrink: 0,
                '&:hover': { color: 'primary.main' },
              }}
            >
              <CodeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography component="span" variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                {t('common.appName')}
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => setSearchOpen(true)}
                color="inherit"
                startIcon={<SearchIcon />}
                aria-label={t('layout.search')}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  width: '100%',
                  maxWidth: 420,
                  justifyContent: 'flex-start',
                  color: 'text.secondary',
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  fontWeight: 400,
                  px: 1.5,
                  '&:hover': { borderColor: 'primary.main', backgroundColor: alpha(theme.palette.text.primary, 0.06) },
                }}
              >
                <Box component="span" sx={{ flexGrow: 1, textAlign: 'left' }}>
                  {t('layout.searchPlaceholder')}
                </Box>
                <Box
                  component="kbd"
                  sx={{ fontFamily: 'inherit', fontSize: 12, px: 0.75, borderRadius: 1, border: 1, borderColor: 'divider' }}
                >
                  {isMac ? '⌘K' : 'Ctrl K'}
                </Box>
              </Button>
            </Box>

            <Tooltip title={t('layout.search')}>
              <IconButton
                color="inherit"
                onClick={() => setSearchOpen(true)}
                aria-label={t('layout.search')}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={themeLabel}>
              <IconButton color="inherit" onClick={toggleMode} aria-label={themeLabel}>
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Button
              color="inherit"
              startIcon={<TranslateIcon />}
              onClick={(e) => setLangAnchor(e.currentTarget)}
              aria-label={t('common.language')}
              aria-haspopup="menu"
              sx={{ minWidth: 0, fontWeight: 700 }}
            >
              {lang.toUpperCase()}
            </Button>
            <Menu anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}>
              {LANGUAGES.map((code) => (
                <MenuItem key={code} selected={code === lang} onClick={() => handleLanguageChange(code)} lang={code}>
                  {LANGUAGE_NAMES[code]}
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 300 } }}
      >
        <Toolbar sx={{ minHeight: HEADER_HEIGHT }} />
        <List component="nav" dense>
          {toolCategories.map((category) => (
            <React.Fragment key={category.id}>
              <ListSubheader sx={{ lineHeight: '36px', fontWeight: 700 }}>{t(`categories.${category.id}`)}</ListSubheader>
              {category.tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <ListItemButton
                    key={tool.id}
                    component={RouterLink}
                    to={pagePath(lang, tool.slug)}
                    selected={isActive(tool.slug)}
                    onClick={() => setMenuOpen(false)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Icon sx={{ color: toolAccent(tool.color, theme.palette.mode) }} />
                    </ListItemIcon>
                    <ListItemText primary={t(`tools.${tool.id}.title`)} />
                  </ListItemButton>
                );
              })}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} lang={lang} />
    </>
  );
};

export default Header;
